import {ref} from "vue";
import {defineStore} from "pinia";
import axios from "axios";
import type {AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig} from "axios";
import type {LoginCallbackResponse} from "@/types/auth";
import {useApiStore} from "@/stores/api";
import {User} from "@/types/users/user";
import type {UserData} from "@/types/users/user";
import {AccountPermissions} from "@/types/permissions/AccountPermission";
import {MaintenanceInfo} from "@/types/MaintenanceInfo";
import {useRouter} from 'vue-router';
import {clearKeyPair, clearNonce, getNonce, makeDpopProof, setNonce} from "@/auth/dpop";
import * as Sentry from "@sentry/vue";

const AUTH_BASE_URL = import.meta.env.VITE_AUTH_BASE_URL ?? '';
const BASE_URL = import.meta.env.VITE_BASE_URL ?? '';

const DEFAULT_TOKEN_LIFETIME_MS = 15 * 60_000;
const PRE_REQUEST_REFRESH_THRESHOLD_MS = 30_000;
const REFRESH_TIMEOUT_MS = 20_000;
const MAX_NONCE_RETRIES = 3;

// Lifetime as a duration (exp - iat) instead of the absolute exp claim — comparing a
// server-issued exp against the local clock would inherit the client's clock offset.
const tokenLifetimeMs = (jwt: string): number => {
    try {
        const payload = JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        const lifetime = (payload.exp - payload.iat) * 1000;
        return Number.isFinite(lifetime) && lifetime > 0 ? lifetime : DEFAULT_TOKEN_LIFETIME_MS;
    } catch {
        return DEFAULT_TOKEN_LIFETIME_MS;
    }
};

type RetryableConfig = InternalAxiosRequestConfig & {
    _dpopNonceRetries?: number;
    _refreshRetry?: boolean;
};

// Fire-and-forget diagnostics into the backend's admin_login log — production users'
// browser consoles are unreachable, so session failures must leave a server-side trace.
// Bare axios: the intercepted instances would try to attach auth to a dying session.
const logSessionEvent = (event: string, context: Record<string, unknown>): void => {
    if (!BASE_URL) return;
    try {
        axios.post(`${BASE_URL}/api/loginEvent`, {event, context}).catch(() => {});
    } catch {}
};

type RefreshOutcome = 'ok' | 'transient' | 'definitive';

export type SessionMessage =
    | { type: 'activity' }
    | { type: 'logout' }
    | { type: 'token_updated'; token: string; ts: number };

let sessionChannel: BroadcastChannel | null = null;
try { sessionChannel = new BroadcastChannel('oneclick-session'); }
catch { sessionChannel = null; }

export function sendSessionMessage(msg: SessionMessage): void {
    try { sessionChannel?.postMessage(msg); }
    catch {}
}

export function onSessionMessage(handler: (msg: SessionMessage) => void): () => void {
    if (!sessionChannel) return () => {};
    const wrapped = (event: MessageEvent<SessionMessage>) => handler(event.data);
    sessionChannel.addEventListener('message', wrapped);
    return () => sessionChannel?.removeEventListener('message', wrapped);
}

export const useAuthStore = defineStore("auth", () => {
    const token = ref<string>();
    const isLoggedIn = ref(false);
    const api = useApiStore();
    const user = ref<User|null>(null);
    const accountPermissions = ref<AccountPermissions>(new AccountPermissions());
    const hiddenMaintenanceNotificationId = ref<number|null>(null);
    const maintenanceInfo = ref<MaintenanceInfo|null>(null);
    const hiddenAdminSiteNotificationIds = ref<Array<number>>([]);
    const router = useRouter();

    let lastTokenSetAt = 0;
    let tokenExpiresAt = 0;
    let refreshInFlight: Promise<RefreshOutcome> | null = null;

    const trackTokenExpiry = (jwt: string, issuedAtMs: number) => {
        tokenExpiresAt = issuedAtMs + tokenLifetimeMs(jwt);
    };

    const ensureFreshToken = async (minRemainingMs: number = PRE_REQUEST_REFRESH_THRESHOLD_MS): Promise<void> => {
        if (!token.value || tokenExpiresAt === 0) return;
        if (Date.now() < tokenExpiresAt - minRemainingMs) return;
        await refreshAccessToken();
    };

    onSessionMessage((msg) => {
        if (msg.type === 'token_updated' && typeof msg.token === 'string' && msg.token !== '') {
            token.value = msg.token;
            lastTokenSetAt = msg.ts ?? Date.now();
            isLoggedIn.value = true;
            trackTokenExpiry(msg.token, lastTokenSetAt);
        }
    });

    const closeMaintenanceNotificationId = (hidden_maintenance_notification_id:number) => {
        hiddenMaintenanceNotificationId.value = hidden_maintenance_notification_id;
        localStorage.setItem('hidden_maintenance_notification_id', hidden_maintenance_notification_id.toString());
    }

    const getClosedMaintenanceNotificationId = () => {
        const notification_id = localStorage.getItem('hidden_maintenance_notification_id');
        hiddenMaintenanceNotificationId.value = notification_id === null ? null : parseInt(notification_id);
    }

    const deleteClosedMaintenanceNotificationId = () => {
        localStorage.removeItem('hidden_maintenance_notification_id');
    }

    const closeSiteNotificationId = (hidden_site_notification_id:number) => {
        hiddenAdminSiteNotificationIds.value.push(hidden_site_notification_id);
        localStorage.setItem('hidden_admin_site_notification_ids', JSON.stringify(hiddenAdminSiteNotificationIds.value));
    }

    const getClosedSiteNotificationIds = () => {
        const notification_ids = localStorage.getItem('hidden_admin_site_notification_ids');
        hiddenAdminSiteNotificationIds.value = notification_ids === null ? [] : JSON.parse(notification_ids);
    }

    const deleteClosedSiteNotificationIds = () => {
        localStorage.removeItem('hidden_admin_site_notification_ids');
    }

    const attachDpopRequest = async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
        if (!config.headers) return config;

        const cfg = config as RetryableConfig;
        const isRetry = (cfg._dpopNonceRetries ?? 0) > 0 || !!cfg._refreshRetry;

        if (config.headers['DPoP'] && !isRetry) return config;
        if (!token.value) return config;

        await ensureFreshToken();

        const tokenSnapshot = token.value;
        if (!tokenSnapshot) return config;
        const proof = await makeDpopProof({
            htu: combineUrl(config.baseURL, config.url),
            htm: (config.method ?? 'get').toUpperCase(),
            accessToken: tokenSnapshot,
            nonce: getNonce() ?? undefined,
        });
        config.headers['Authorization'] = `DPoP ${tokenSnapshot}`;
        config.headers['DPoP'] = proof;
        return config;
    };

    const combineUrl = (baseURL: string | undefined, requestUrl: string | undefined): string => {
        const url = requestUrl ?? '';
        if (/^https?:\/\//i.test(url)) return url.split('?')[0].split('#')[0];
        const base = (baseURL ?? window.location.origin).replace(/\/+$/, '');
        const path = url.replace(/^\/+/, '');
        return (base + '/' + path).split('?')[0].split('#')[0];
    };

    const captureNonceFromResponse = (response: AxiosResponse): AxiosResponse => {
        const nonce = response.headers?.['dpop-nonce'];
        if (typeof nonce === 'string') setNonce(nonce);
        return response;
    };

    const makeErrorHandler = (instance: AxiosInstance) => async (error: AxiosError): Promise<unknown> => {
        const response = error.response;
        const config = error.config as RetryableConfig | undefined;
        if (!config || !response) return Promise.reject(error);

        const headerNonce = response.headers?.['dpop-nonce'];
        if (typeof headerNonce === 'string') setNonce(headerNonce);

        const data = response.data as { error?: string } | undefined;

        if (response.status === 401 && data?.error === 'use_dpop_nonce' && (config._dpopNonceRetries ?? 0) < MAX_NONCE_RETRIES) {
            config._dpopNonceRetries = (config._dpopNonceRetries ?? 0) + 1;
            return instance.request(config);
        }

        if (
            response.status === 401
            && !config._refreshRetry
            && token.value
            && data?.error !== 'invalid_dpop_proof'
            && data?.error !== 'use_dpop_nonce'
        ) {
            config._refreshRetry = true;
            const outcome = await runRefresh();
            if (outcome === 'ok') return instance.request(config);
            if (outcome === 'definitive') {
                logSessionEvent('forced_logout', {
                    request_url: config.url,
                    request_method: config.method,
                    request_status: response.status,
                    request_error: data?.error,
                });
                sendSessionMessage({ type: 'logout' });
                await localLogout();
            }
        }

        return Promise.reject(error);
    };

    api.axiosApi.interceptors.request.use(attachDpopRequest);
    api.axiosAuth.interceptors.request.use(attachDpopRequest);
    api.axiosApi.interceptors.response.use(captureNonceFromResponse, makeErrorHandler(api.axiosApi));
    api.axiosAuth.interceptors.response.use(captureNonceFromResponse, makeErrorHandler(api.axiosAuth));

    const doRefresh = async (): Promise<RefreshOutcome> => {
        if (token.value && Date.now() - lastTokenSetAt < 5000) {
            isLoggedIn.value = true;
            return 'ok';
        }
        try {
            const proof = await makeDpopProof({
                htu: `${AUTH_BASE_URL}/oauth/token`,
                htm: 'POST',
                nonce: getNonce() ?? undefined,
            });
            const response = await axios.post(`${BASE_URL}/api/refresh`, null, {
                withCredentials: true,
                headers: {DPoP: proof},
                timeout: REFRESH_TIMEOUT_MS,
            });
            const newNonce = response.headers?.['dpop-nonce'];
            if (typeof newNonce === 'string') setNonce(newNonce);
            const newToken: string | undefined = response.data?.access_token;
            token.value = newToken;
            isLoggedIn.value = !!newToken;
            if (newToken) {
                lastTokenSetAt = Date.now();
                trackTokenExpiry(newToken, lastTokenSetAt);
                sendSessionMessage({ type: 'token_updated', token: newToken, ts: lastTokenSetAt });
                return 'ok';
            }
            return 'transient';
        } catch (e) {
            const err = e as AxiosError;
            const newNonce = err.response?.headers?.['dpop-nonce'];
            if (typeof newNonce === 'string') {
                setNonce(newNonce);
                if (err.response?.status === 401 && (err.response.data as {error?: string})?.error === 'use_dpop_nonce') {
                    return doRefresh();
                }
            }
            const status = err.response?.status;
            const errorCode = (err.response?.data as {error?: string} | undefined)?.error;
            if ((status === 400 || status === 401) && errorCode !== 'use_dpop_nonce' && errorCode !== 'missing_refresh_cookie') {
                console.error('session refresh rejected definitively', status, err.response?.data);
                logSessionEvent('refresh_rejected', {
                    status,
                    error: errorCode,
                    body: JSON.stringify(err.response?.data ?? null).slice(0, 500),
                });
                return 'definitive';
            }
            return 'transient';
        }
    };

    const runRefresh = async (): Promise<RefreshOutcome> => {
        if (refreshInFlight) return refreshInFlight;
        refreshInFlight = (async () => {
            try {
                if (typeof navigator?.locks?.request === 'function') {
                    return await navigator.locks.request('dpop-refresh', doRefresh);
                }
                return await doRefresh();
            } finally {
                refreshInFlight = null;
            }
        })();
        return refreshInFlight;
    };

    const refreshAccessToken = async (): Promise<boolean> => {
        if (!BASE_URL || !AUTH_BASE_URL) {
            console.warn('Cannot refresh: VITE_BASE_URL or VITE_AUTH_BASE_URL is not set');
            return false;
        }
        return (await runRefresh()) === 'ok';
    };

    const initApp = async (options: { skipRefresh?: boolean } = {}) => {
        getClosedMaintenanceNotificationId();
        getClosedSiteNotificationIds();
        if (!options.skipRefresh && localStorage.getItem('has_session') === '1') {
            const outcome = await runRefresh();
            if (outcome === 'definitive') localStorage.removeItem('has_session');
        }
        accountPermissions.value.permissionList = [];
    };

    const loginUser = (user_data: LoginCallbackResponse) => {
        token.value = user_data.access_token;
        isLoggedIn.value = !!token.value;
        if (token.value) {
            localStorage.setItem('has_session', '1');
            lastTokenSetAt = Date.now();
            trackTokenExpiry(token.value, lastTokenSetAt);
        }
        localStorage.removeItem('last_activity_at');

        deleteClosedMaintenanceNotificationId();
        deleteClosedSiteNotificationIds();

        accountPermissions.value.permissionList = [];
    };

    const setUser = (_user:UserData) => {
        Sentry.setUser({id: String(_user.id)});
        Sentry.setTag('principal_type', 'user');
        Sentry.setAttributes({'principal_type': 'user'});

        user.value = new User();
        user.value.id = _user.id;
        user.value.first_name = _user.first_name;
        user.value.last_name = _user.last_name;
        user.value.image_data = _user.image_data;
        user.value.email = _user.email;
        user.value.raws_per_page_chart_dashboard = _user.raws_per_page_chart_dashboard;
        user.value.isDomesticResponsible = _user.isDomesticResponsible;
        user.value.isInternationalResponsible = _user.isInternationalResponsible;
        user.value.isStrongoResponsible = _user.isStrongoResponsible;
        user.value.image_type = _user.image_type;
        user.value.image_contents = _user.image_contents;
        user.value.maintenanceInfo = false;
        user.value.lastReleaseRead = _user.lastReleaseRead;
        if (_user.maintenance !== null && _user.maintenance !== undefined && _user.maintenance !== false) {
            user.value.maintenanceInfo = new MaintenanceInfo(_user.maintenance);
        }

        if(_user.accountPermissions !== undefined)
        {
            accountPermissions.value = new AccountPermissions(_user.accountPermissions);
        }
    };

    const localLogout = async () => {
        tokenExpiresAt = 0;
        token.value = undefined;
        isLoggedIn.value = false;
        user.value = null;
        accountPermissions.value.permissionList = [];
        localStorage.clear();
        deleteClosedMaintenanceNotificationId();
        deleteClosedSiteNotificationIds();
        await clearKeyPair();
        clearNonce();
        Sentry.setUser(null);
        Sentry.setTag('principal_type', undefined);
        Sentry.getIsolationScope().removeAttribute('principal_type');
        window.location.href = router.resolve({name: 'login'}).href;
    };

    const logoutUser = async () => {
        sendSessionMessage({ type: 'logout' });
        try {
            await api.axiosApi.get(`${import.meta.env.VITE_BASE_URL}/api/user/logout`);
        } catch (e) {
            console.log('logout call failed', e);
        }
        await localLogout();
    };

    return {token,
        isLoggedIn,
        user,
        initApp,
        loginUser,
        setUser,
        logoutUser,
        localLogout,
        refreshAccessToken,
        ensureFreshToken,
        accountPermissions,
        maintenanceInfo,
        hiddenMaintenanceNotificationId,
        closeMaintenanceNotificationId,
        getClosedMaintenanceNotificationId,
        deleteClosedMaintenanceNotificationId,
        hiddenAdminSiteNotificationIds,
        closeSiteNotificationId,
        getClosedSiteNotificationIds,
        deleteClosedSiteNotificationIds,
    };
});
